import { apiCall, apiCallWithMeta } from '@/lib/api-client';
import type { Product } from './types';
import { UPLOAD_BATCH_TIMEOUT_MS, type BatchedUploadResult, type UploadProgress } from './upload-batches';
import { uploadDirect, type DirectUploadTarget } from './upload-direct';

/**
 * GET /api/products — returns the current user's submitted products.
 *
 * Backend filters to `status='submitted'` automatically (drafts don't show).
 * Used by every list page (Predicate Finder, Regulatory Strategy, Clinical
 * Protocols, etc.) since they all operate on the same product roster.
 */
export async function getProducts(): Promise<Product[]> {
  return apiCall<Product[]>('/api/products', { method: 'GET' });
}

/** How the IVD verdict was decided (mirrors the backend ``VerdictSource``). */
export type IvdSource = 'override' | 'code' | 'confirmed' | 'unresolved';

/** The product's current effective IVD verdict + how it was decided.
 *  ``is_ivd`` is null when the device is UNCLASSIFIED (no code/confirmation/
 *  override) — there's no silent default, so the UI must require a pick.
 *  ``updated_at`` is echoed back as ``If-Match`` on the next write. */
export interface IvdOverride {
  is_ivd: boolean | null;
  source: IvdSource;
  updated_at: string;
}

/**
 * GET /api/products/{id}/ivd-override — the verdict the dossier dispatch toggle
 * defaults to: explicit override > the verdict saved when the code was captured
 * > the §809.3 confirmation > non-IVD. Device-only; never hits openFDA.
 */
export async function getIvdOverride(productId: number): Promise<IvdOverride> {
  return apiCall<IvdOverride>(`/api/products/${productId}/ivd-override`, {
    method: 'GET',
  });
}

/**
 * POST /api/products/{id}/ivd-override — persist the user's deliberate
 * IVD/non-IVD choice (write-back) so dossier generation produces the chosen
 * eSTAR format. Returns the new effective verdict (with a fresh ``updated_at``).
 *
 * ``ifMatch`` is the previous ``updated_at`` (from the GET) — sent as the
 * ``If-Match`` header for optimistic concurrency; a stale value yields a 409.
 */
export async function setIvdOverride(
  productId: number,
  isIvd: boolean,
  ifMatch?: string,
): Promise<IvdOverride> {
  return apiCall<IvdOverride>(`/api/products/${productId}/ivd-override`, {
    method: 'POST',
    json: { is_ivd: isIvd },
    headers: ifMatch ? { 'If-Match': ifMatch } : undefined,
  });
}

/** The product's stored {country: submission_type} map + its concurrency token. */
export interface SubmissionPathways {
  pathways: Record<string, string>;
  updated_at: string;
}

/** GET /api/products/{id}/submission-pathways — the Step-2 country/pathway map. */
export async function getSubmissionPathways(
  productId: number,
): Promise<SubmissionPathways> {
  return apiCall<SubmissionPathways>(
    `/api/products/${productId}/submission-pathways`,
  );
}

/** POST /api/products/{id}/submission-pathways — persist the Step-2 pick. */
export async function setSubmissionPathways(
  productId: number,
  pathways: Record<string, string>,
  ifMatch?: string,
): Promise<SubmissionPathways> {
  return apiCall<SubmissionPathways>(
    `/api/products/${productId}/submission-pathways`,
    {
      method: 'POST',
      json: { pathways },
      headers: ifMatch ? { 'If-Match': ifMatch } : undefined,
    },
  );
}

/** One UDI-DI and what it identifies (Annex VI Part C: a UDI-DI per configuration and packaging level),
 *  and the Basic UDI-DI (device model) it belongs to — '' when not chosen. */
export interface UdiDiRow {
  udi_di: string;
  configuration: string;
  packaging_level: string;
  basic_udi_di: string;
}

/** A designated UDI issuing entity (MDR / IVDR Art. 27(2) / 24(2)) — each sets its own format. */
export type UdiIssuingEntity = 'GS1' | 'HIBCC' | 'ICCBBA' | 'IFA';

/** The device's EUDAMED registration (UDI / Device module) — RA acceptance checklist B.4. */
export type EudamedStatus = 'registered' | 'not_registered';

/** A device's EU identifiers (MDR / IVDR Art. 27) — a Basic UDI-DI per device model (most products
 *  have one) and one row per UDI-DI. */
export interface EuIdentifiers {
  basic_udi_dis: string[];
  issuing_entity: UdiIssuingEntity | null;
  eudamed_status: EudamedStatus | null;
  udi_dis: UdiDiRow[];
}

/** GET /api/products/{id}/eu-identifiers */
export async function getEuIdentifiers(productId: number): Promise<EuIdentifiers> {
  return apiCall<EuIdentifiers>(`/api/products/${productId}/eu-identifiers`);
}

/** POST /api/products/{id}/eu-identifiers — blank / null clears one. Device products only. */
export async function setEuIdentifiers(productId: number, ids: EuIdentifiers): Promise<EuIdentifiers> {
  return apiCall<EuIdentifiers>(`/api/products/${productId}/eu-identifiers`, { method: 'POST', json: ids });
}

/** One keyset-paginated page of the products list. The backend carries the
 *  paging state in headers (X-Next-Cursor / X-Total-Count) so the body
 *  stays a plain Product[]. */
export interface ProductsPage {
  items: Product[];
  /** Opaque cursor for the next page; null on the last page. */
  nextCursor: string | null;
  /** Rows matching the active FILTERS (q / eligibility), not the whole
   *  account — drives the per-filter-accurate "Showing X of Y" footer. */
  totalCount: number | null;
}

/** Server-side sort keys for the products list. ``name`` puts no-name
 *  products last in either direction; id is always the tiebreaker. */
// 'status' orders the ROSTER by its extraction badge's SEVERITY (errors first ascending),
// ranked on the status values rather than their labels — alphabetically 'Approved' would
// come first, inverting the useful order.
//
// The 'status_*' keys order the FINDER tables, whose Status column is a different thing:
// that agent's run state (Not started / Running / Analyzed). One key per agent because
// every finder table shares this endpoint and each shows only its own agent.
export type ProductsSortKey =
  | 'submitted'
  | 'name'
  | 'type'
  | 'status'
  | 'status_predicate'
  | 'status_strategy'
  | 'status_dossier'
  | 'status_protocol'
  | 'status_report';

/** The product types the roster's type filter offers. Narrower than
 *  ``ProductType``: 'combination' exists in the frontend union but is not a
 *  value the API accepts (backend ``ProductType`` is drug|biologic|device). */
export type ProductTypeFilter = 'device' | 'drug' | 'biologic';

export interface ProductsPageParams {
  limit: number;
  cursor?: string | null;
  /** Case-insensitive name-contains filter. */
  q?: string;
  /** Defaults server-side to submitted/desc (name+type default asc). */
  sort?: ProductsSortKey;
  dir?: 'asc' | 'desc';
  /** 'finder' restricts to products the Predicate Finder lists (devices on
   *  a Traditional/Abbreviated 510(k) pathway) — server-side, so the page
   *  paginates without fetching the whole roster.
   *  'finder_awaiting_classification' restricts to devices the finder HIDES
   *  only because a market is still marked "Not sure yet" — the finder
   *  page's "Awaiting classification" callout. */
  eligibleFor?: 'finder' | 'finder_awaiting_classification';
  /** Restrict to products whose tech-spec fact audit is approved (or that have no
   *  extraction at all). The agent dispatch tables use this so an un-reviewed,
   *  still-extracting product isn't offered as a run target. */
  dispatchReady?: boolean;
  /** Narrow to one product type — server-side, so the keyset pages and the
   *  X-Total-Count footer both reflect it. */
  productType?: ProductTypeFilter;
  /** List archived products instead of active ones. */
  archived?: boolean;
}

export async function getProductsPage({
  limit,
  cursor,
  q,
  sort,
  dir,
  eligibleFor,
  dispatchReady,
  productType,
  archived,
}: ProductsPageParams): Promise<ProductsPage> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  if (q) params.set('q', q);
  if (sort) params.set('sort', sort);
  if (dir) params.set('dir', dir);
  if (eligibleFor) params.set('eligible_for', eligibleFor);
  if (dispatchReady) params.set('dispatch_ready', 'true');
  if (productType) params.set('product_type', productType);
  if (archived) params.set('archived', 'true');
  const { data, headers } = await apiCallWithMeta<Product[]>(
    `/api/products?${params.toString()}`,
    { method: 'GET' },
  );
  const total = headers.get('X-Total-Count');
  return {
    items: data,
    nextCursor: headers.get('X-Next-Cursor'),
    totalCount: total != null ? Number(total) : null,
  };
}

/** Accurate list-page tile counts, independent of pagination. ``analyzed``
 *  / ``running`` count DISTINCT products per agent (keys: predicate /
 *  strategy / protocol / report / dossier). */
export interface ProductsStats {
  total: number;
  analyzed: Record<string, number>;
  running: Record<string, number>;
  /** Submitted products per product_type (device / drug / biologic). */
  by_type: Record<string, number>;
  /** Products the Predicate Finder page lists — finder-eligible AND
   *  dispatch-ready, the pair of filters that page asks for. The predicate
   *  entries in ``analyzed``/``running`` are scoped to this same set. */
  finder_eligible: number;
  /** Products the four agent rosters (Strategy / Protocols / Reports /
   *  Dossier) list — dispatch-ready ones. Their "Products" badge reads THIS,
   *  not ``total``: a product whose tech-spec extraction is still unreviewed
   *  is in ``total`` but withheld from those tables. */
  dispatch_ready: number;
  /** Products whose latest report is behind the product (the "N market
   *  updates" chip) — the agent pages' "Market updates" tile. Keyed predicate /
   *  strategy / protocol / report. No ``dossier`` key: dossier rows carry a
   *  run-state chip, not a freshness chip, so there is no such state to count. */
  market_updates: Record<string, number>;
}

export async function getProductsStats(): Promise<ProductsStats> {
  return apiCall<ProductsStats>('/api/products/stats', { method: 'GET' });
}

// ── Product supporting documents (free-form, many-per-product) ───────────

export interface SupportingDocument {
  id: number;
  filename: string;
  content_type: string;
  size_bytes: number;
  /** "ok" (converted to markdown), "native_pdf", or "failed: <reason>". */
  conversion_status: string;
  uploaded_at: string;
  /** Characters of converted text the agents read; null while converting / unreadable. */
  text_chars?: number | null;
}

/**
 * The active supporting-document quota for one product, and how much of it is used.
 * Server-owned so the numbers shown always match the ones enforced — `max_files` and
 * `max_total_bytes` are admin-dialable and bound a single upload as well as the
 * product total.
 */
export interface SupportingDocumentLimits {
  max_files: number;
  max_total_bytes: number;
  max_file_bytes: number;
  used_files: number;
  used_bytes: number;
  /** Converted text across the product's documents, and the most an agent can read
   *  in one run (the AI model's context window). Over it, a run is refused. */
  text_chars_total?: number;
  agent_text_budget?: number;
}

export async function getSupportingDocumentLimits(
  productId: number,
): Promise<SupportingDocumentLimits> {
  return apiCall<SupportingDocumentLimits>(
    `/api/products/${productId}/supporting-documents/limits`,
    { method: 'GET' },
  );
}

export async function listSupportingDocuments(
  productId: number,
): Promise<SupportingDocument[]> {
  return apiCall<SupportingDocument[]>(
    `/api/products/${productId}/supporting-documents`,
    { method: 'GET' },
  );
}

export async function uploadSupportingDocuments(
  productId: number,
  files: File[],
): Promise<SupportingDocument[]> {
  const form = new FormData();
  for (const f of files) form.append('files', f);
  return apiCall<SupportingDocument[]>(
    `/api/products/${productId}/supporting-documents`,
    { method: 'POST', bodyRaw: form, timeoutMs: UPLOAD_BATCH_TIMEOUT_MS },
  );
}

// Direct browser→storage supporting-document uploads (see upload-direct.ts for the
// flow). init: the server checks the selection against the quota and returns one
// target per file; the bytes go to storage (presigned S3 form, or through the app in
// local dev); finalize verifies what landed and creates the row.

export interface SupportingDocUploadInitResponse {
  uploads: DirectUploadTarget[];
  max_file_bytes: number;
}

export async function initSupportingDocumentUploads(
  productId: number,
  files: readonly File[],
): Promise<DirectUploadTarget[]> {
  const res = await apiCall<SupportingDocUploadInitResponse>(
    `/api/products/${productId}/supporting-documents/direct/init`,
    {
      method: 'POST',
      json: { files: files.map((f) => ({ filename: f.name, size_bytes: f.size })) },
      timeoutMs: 30_000,
    },
  );
  return res.uploads;
}

/** Local-dev delivery (no presigned form): the bytes go through the app to the key
 *  init reserved. */
export async function uploadSupportingDocumentBlob(
  productId: number,
  target: DirectUploadTarget,
  file: File,
): Promise<void> {
  const form = new FormData();
  form.append('file', file);
  const key = encodeURIComponent(target.storage_key);
  await apiCall<{ storage_key: string }>(
    `/api/products/${productId}/supporting-documents/direct/blob?storage_key=${key}`,
    { method: 'POST', bodyRaw: form, timeoutMs: UPLOAD_BATCH_TIMEOUT_MS },
  );
}

export async function finalizeSupportingDocumentUpload(
  productId: number,
  target: DirectUploadTarget,
): Promise<SupportingDocument> {
  return apiCall<SupportingDocument>(
    `/api/products/${productId}/supporting-documents/direct/finalize`,
    {
      method: 'POST',
      json: { filename: target.filename, storage_key: target.storage_key },
      timeoutMs: 60_000,
    },
  );
}

/** The direct upload flow wired to this product's endpoints. `uploadToPresignedS3`
 *  posts the file with the SERVER-derived content type the policy was signed for
 *  (a browser's `file.type` for CSV varies and would be refused by S3). */
export function uploadSupportingDocumentsDirect(
  productId: number,
  files: readonly File[],
  onProgress?: (p: UploadProgress) => void,
  onFileDone?: () => void,
): Promise<BatchedUploadResult<SupportingDocument>> {
  return uploadDirect<SupportingDocument>(
    files,
    {
      init: (fs) => initSupportingDocumentUploads(productId, fs),
      toStorage: (target, file, contentType) =>
        uploadToPresignedS3(target.presigned as { url: string; fields: Record<string, string> }, file, undefined, contentType),
      throughApp: (target, file) => uploadSupportingDocumentBlob(productId, target, file),
      finalize: (target) => finalizeSupportingDocumentUpload(productId, target),
    },
    onProgress,
    onFileDone,
  );
}

export async function deleteSupportingDocument(
  productId: number,
  docId: number,
): Promise<null> {
  return apiCall<null>(
    `/api/products/${productId}/supporting-documents/${docId}`,
    { method: 'DELETE', allow204: true },
  );
}

export interface SupportingDocMarkdown {
  filename: string;
  content_type: string;
  conversion_status: string;
  /** The converted text the agent will read. NULL for PDFs (read natively). */
  markdown: string | null;
  /** Chunked: this page starts at `offset`; `next_offset` is null on the last chunk. */
  offset: number;
  next_offset: number | null;
  total_chars: number | null;
}

export async function getSupportingDocMarkdown(
  productId: number,
  docId: number,
  offset = 0,
): Promise<SupportingDocMarkdown> {
  return apiCall<SupportingDocMarkdown>(
    `/api/products/${productId}/supporting-documents/${docId}/markdown?offset=${offset}`,
    { method: 'GET' },
  );
}

/** URL to download the original uploaded supporting document (cookie-authed,
 *  same-origin) — used with an <a download> link. */
export function supportingDocumentDownloadUrl(productId: number, docId: number): string {
  return `/api/products/${productId}/supporting-documents/${docId}`;
}

/**
 * Snapshot returned by ``GET /api/products/{id}/edit`` — full ``data``
 * plus ``updated_at`` (which the caller must echo as ``If-Match`` on the
 * follow-up PUT for optimistic concurrency).
 */
export interface ProductEditSnapshot {
  product_id: number;
  product_type: string;
  status: string;
  data: Record<string, unknown>;
  /** The reviewer's kept-sections markdown when the product was section-approved. null while
   *  the spec is in review, or for a legacy product left unconverted. */
  tech_spec_markdown: string | null;
  /** Section-review view gate, folded into this snapshot so the edit page needn't fetch the
   *  source_markdown-heavy proposal: the roster's badge status of the latest extraction
   *  proposal (null = no reviewable proposal / legacy product). */
  extraction_status: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * GET /api/products/{id}/edit — fetch a submitted product for editing.
 *
 * Owner-checked at the backend. 404 for missing or non-owned ids — the
 * caller surfaces a "this product can't be edited" UI.
 */
export async function getProductForEdit(
  productId: number,
): Promise<ProductEditSnapshot> {
  return apiCall<ProductEditSnapshot>(`/api/products/${productId}/edit`, {
    method: 'GET',
  });
}

/**
 * PUT /api/products/{id} — amend a submitted product in place.
 *
 * ``ifMatch`` is the previous ``updated_at`` from the edit snapshot. The
 * literal ``"*"`` skips the concurrency check (use when the caller has
 * explicitly chosen to overwrite). Returns the new snapshot so callers
 * can refresh their ``ifMatch`` value for the next save.
 */
export async function amendProduct(
  productId: number,
  data: Record<string, unknown>,
  ifMatch: string,
): Promise<ProductEditSnapshot> {
  return apiCall<ProductEditSnapshot>(`/api/products/${productId}`, {
    method: 'PUT',
    json: { data },
    headers: { 'If-Match': ifMatch },
  });
}

/**
 * DELETE /api/products/{id} — soft-archive (status='archived').
 *
 * Idempotent (204 even on re-archive). Backend keeps the row + audit
 * history; only the list endpoint filters it out.
 */
export async function archiveProduct(productId: number): Promise<null> {
  return apiCall<null>(`/api/products/${productId}`, {
    method: 'DELETE',
    allow204: true,
  });
}

/**
 * POST /api/products/{id}/restore — un-archive (status='archived' → 'submitted').
 *
 * Inverse of archiveProduct. Idempotent (204 even if already submitted).
 */
export async function restoreProduct(productId: number): Promise<null> {
  return apiCall<null>(`/api/products/${productId}/restore`, {
    method: 'POST',
    allow204: true,
  });
}

export interface UploadProductDocumentArgs {
  file: File;
  productType: 'device' | 'drug' | 'biologic';
}

export interface UploadProductDocumentResponse {
  product_id: number;
  /** Always true: the upload stages an extraction proposal for the fact audit
   *  (it does NOT write facts into the product until the user approves). */
  review: boolean;
  /** Poll cadence for the fact-audit screen while extraction runs. */
  poll_ms: number;
  max_polls: number;
}

/**
 * POST /api/products/upload-document — create a product from an uploaded
 * PDF/DOCX tech-spec and stage a confidence-scored extraction proposal in the
 * background. Returns the new ``product_id``; the caller sends the user to the
 * fact-audit screen (``/product-account/{id}/review``) to approve what gets
 * written into the product. Max 10 MB; rejects non-PDF/DOCX.
 */
export async function uploadProductDocument(
  args: UploadProductDocumentArgs,
): Promise<UploadProductDocumentResponse> {
  const form = new FormData();
  form.append('file', args.file);
  form.append('product_type', args.productType);
  return apiCall<UploadProductDocumentResponse>('/api/products/upload-document', {
    method: 'POST',
    bodyRaw: form,
  });
}

// ── Direct browser→S3 upload (the big-file path) ────────────────────────────
// init reserves a product + storage key and returns a presigned S3 POST target
// (staging) or null (local dev → fall back to the through-app upload above);
// finalize confirms the upload landed + kicks extraction.

export interface UploadDocumentInitResponse {
  product_id: number;
  storage_key: string;
  /** Presigned S3 POST target, or null when the backend can't mint one (local
   *  dev on LocalStorage) — then the client uploads through the app instead. */
  presigned: { url: string; fields: Record<string, string> } | null;
  max_bytes: number;
}

function mimeForFile(file: File): string {
  if (file.type) return file.type;
  const n = file.name.toLowerCase();
  if (n.endsWith('.pdf')) return 'application/pdf';
  if (n.endsWith('.docx'))
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  return 'application/octet-stream';
}

export async function initDocumentUpload(
  args: UploadProductDocumentArgs,
): Promise<UploadDocumentInitResponse> {
  return apiCall<UploadDocumentInitResponse>('/api/products/upload-document/init', {
    method: 'POST',
    json: { product_type: args.productType, filename: args.file.name, content_type: mimeForFile(args.file) },
  });
}

export async function finalizeDocumentUpload(
  productId: number,
): Promise<UploadProductDocumentResponse> {
  return apiCall<UploadProductDocumentResponse>('/api/products/upload-document/finalize', {
    method: 'POST',
    json: { product_id: productId },
  });
}

/** POST the file DIRECTLY to S3 via the presigned policy. Uses XHR (not fetch)
 *  because only XHR reports upload progress — essential for a multi-hundred-MB
 *  file. S3 answers 204 on success. */
export function uploadToPresignedS3(
  presigned: { url: string; fields: Record<string, string> },
  file: File,
  onProgress?: (fraction: number) => void,
  contentType?: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    for (const [k, v] of Object.entries(presigned.fields)) form.append(k, v);
    // The signed policy conditions on Content-Type: when the server names the type it
    // signed for, send the bytes under THAT type (a browser's `file.type` varies, and a
    // mismatch makes S3 refuse the upload).
    const blob = contentType && file.type !== contentType ? new Blob([file], { type: contentType }) : file;
    form.append('file', blob, file.name); // the file field MUST be last in an S3 POST
    const xhr = new XMLHttpRequest();
    xhr.open('POST', presigned.url);
    // Same bound as the through-app path. Without it a stalled connection never errors and
    // the dialog sits at "Uploading… 0 of N" until the user gives up and refreshes.
    xhr.timeout = UPLOAD_BATCH_TIMEOUT_MS;
    xhr.ontimeout = () => reject(new Error('Upload to storage timed out. Check your connection and try again.'));
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(e.loaded / e.total);
      };
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload to storage failed (${xhr.status}).`));
    xhr.onerror = () => reject(new Error('Upload to storage failed (network error).'));
    xhr.send(form);
  });
}

/** Upload the file through the app to the product + key that init already
 *  reserved (the LocalStorage/dev fallback — no presigned URL). Reuses the
 *  init-created product instead of creating a second one. */
export async function uploadDocumentBlob(productId: number, file: File): Promise<void> {
  const form = new FormData();
  form.append('file', file);
  await apiCall<{ product_id: number }>(`/api/products/upload-document/${productId}/blob`, {
    method: 'POST',
    bodyRaw: form,
  });
}

/** Unified upload: direct browser→S3 when the backend offers a presigned URL
 *  (staging), otherwise through the app to the SAME init-created product (local
 *  dev). Both paths are init → deliver bytes → finalize, so neither creates a
 *  duplicate product. Same result shape either way. */
export async function uploadProductDocumentDirect(
  args: UploadProductDocumentArgs,
  onProgress?: (fraction: number) => void,
): Promise<UploadProductDocumentResponse> {
  const init = await initDocumentUpload(args);
  if (init.presigned) {
    await uploadToPresignedS3(init.presigned, args.file, onProgress);
  } else {
    await uploadDocumentBlob(init.product_id, args.file); // local dev, no double-create
  }
  return finalizeDocumentUpload(init.product_id);
}
